import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { clsx } from 'clsx';
import DOMPurify from 'dompurify';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntervalWhen } from 'rooks';
import { DEFAULT_TOOL_TIP_DELAY } from './Tooltip.constants';
import type { Placement } from '@floating-ui/dom';

const getArrowStyle = (
  position: string,
  x: number | undefined,
  arrowWidth: number,
  tooltipHeight: number,
  tooltipWidth: number,
) => {
  switch (position) {
    case 'bottom':
      return {
        left: `${x}px`,
        top: `${-arrowWidth}px`,
      };

    case 'left':
      return {
        left: `${tooltipWidth - arrowWidth}px`,
        top: `${tooltipHeight / 2 - arrowWidth}px`,
      };
    case 'right':
      return {
        left: `${-arrowWidth}px`,
        top: `${tooltipHeight / 2 - arrowWidth}px`,
      };
    default: // 'top':
      return {
        left: `${x}px`,
        top: `${tooltipHeight - 10 + arrowWidth}px`,
      };
  }
};

type HTMLTipProps = {
  text: string;
};

const HTMLTip = ({ text }: HTMLTipProps) => {
  // oxlint-disable-next-line react/no-danger
  return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }} />;
};

// Tooltips can cause system tests to be flaky, so we suppress them in Playwright and Protractor.
const suppressTooltip = () => {
  return window.navigator.userAgent === 'Playwright' || window.navigator.userAgent.includes('Protractor');
};

type TooltipState = {
  target: HTMLElement;
  text: string;
  html: boolean;
  testId?: string;
  placement: Placement;
  originalPosition: DOMRect;
};

/**
 * QTip
 *
 * QTip is a Singleton Tooltip component that guarantees high-performance and reduces component wrappers!
 *
 * QTip is used by all qomponents that support the display of tooltips.
 * If you want to add a Tooltip to your application you can do so by adding the following html data-attributes:
 *
 *    'data-qtip-text': the tooltip text to display; this can also be a string containing valid HTML
 *    'data-qtip-placement': one of TooltipPosition (top, bottom, right, or left)
 *    'data-qtip-is-html': set this to true if you provided a text that contains HTML,
 *    'data-qtip-delay': this can be used to delay the showing of the tooltip. this should be a number representing
 *    the # of milliseconds you want to delay the tooltip for. If no delay is provided the DEFAULT_TOOL_TIP_DELAY is
 *    applied!
 *    'data-qtip-testid': use this attribute to provide a value for a data-testid of your tooltip; this is useful
 *    for tests
 *
 * In order for QTip to be able to display Tooltips you must add the QTip component to your top-most component
 * (often that's App or Application) - simply add:
 *
 *    <QTip />
 *
 * and enjoy beautiful & performant tooltips!
 */
export const QTip = () => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const tooltipArrowRef = useRef<HTMLDivElement>(null);

  // Bundling content + target into a single state keeps the render and the
  // positioning pass in sync — computePosition always measures the DOM that
  // matches the state it was invoked for.
  const [tooltipState, setTooltipState] = useState<TooltipState | null>(null);
  const [show, setShow] = useState(false);
  const [overTooltip, setOverTooltip] = useState(false);

  // Runs after React commits the new content, so computePosition measures the
  // current tooltip dimensions, not the previously shown tooltip's.
  useLayoutEffect(() => {
    if (!tooltipState || !tooltipRef.current || !tooltipArrowRef.current) {
      return;
    }
    let cancelled = false;
    // oxlint-disable-next-line typescript/no-floating-promises
    computePosition(tooltipState.target, tooltipRef.current, {
      placement: tooltipState.placement,
      middleware: [offset(10), flip(), shift({ padding: 8 }), arrow({ element: tooltipArrowRef.current })],
    }).then(({ x, y, middlewareData, placement: finalPlacement }) => {
      if (cancelled || !tooltipRef.current || !tooltipArrowRef.current) {
        return;
      }
      Object.assign(tooltipRef.current.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
      if (middlewareData.arrow) {
        const { x: arrowX, y: arrowY } = middlewareData.arrow;
        const arrowWidth = tooltipArrowRef.current.offsetHeight / 2;
        const tooltipHeight = tooltipRef.current.offsetHeight;
        const tooltipWidth = tooltipRef.current.offsetWidth;
        const style = getArrowStyle(finalPlacement, arrowX ?? arrowY, arrowWidth, tooltipHeight, tooltipWidth);
        Object.assign(tooltipArrowRef.current.style, style);
      }
      setShow(true);
    });
    return () => {
      cancelled = true;
    };
  }, [tooltipState]);

  // Check every 300ms if the tooltip target position has changed and hide the tooltip if this happens.
  useIntervalWhen(
    () => {
      if (overTooltip || !tooltipState) {
        return;
      }
      const currentPosition = tooltipState.target.getBoundingClientRect();
      if (
        tooltipState.originalPosition.y !== currentPosition.y ||
        tooltipState.originalPosition.x !== currentPosition.x
      ) {
        setShow(false);
      }
    },
    300,
    show,
  );

  const ttTimeout = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent): void => {
      if (ttTimeout.current) {
        clearTimeout(ttTimeout.current);
      }

      const tooltipElement = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-qtip-text]') : null;

      if (!tooltipElement) {
        // Cursor left a qtip target — hide unless it's now over the tooltip itself.
        const cursorOverTooltip = e.target instanceof Node && tooltipRef.current?.contains(e.target);
        if (!cursorOverTooltip) {
          setShow(false);
        }
        return;
      }

      const text = tooltipElement.dataset.qtipText;
      if (!text) {
        return;
      }

      const delay = parseInt(tooltipElement.dataset.qtipDelay || `${DEFAULT_TOOL_TIP_DELAY}`, 10);
      const placement = (tooltipElement.dataset.qtipPlacement || 'top') as Placement;
      const isHtml = tooltipElement.dataset.qtipIsHtml === 'true';
      const testId = tooltipElement.dataset.qtipTestid;

      ttTimeout.current = setTimeout(() => {
        setTooltipState({
          target: tooltipElement,
          text,
          html: isHtml,
          testId,
          placement,
          originalPosition: tooltipElement.getBoundingClientRect(),
        });
      }, delay);
    };

    !suppressTooltip() && document.body.addEventListener('mousemove', onMouseMove);
    return () => {
      !suppressTooltip() && document.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  return (
    <>
      <div
        onMouseEnter={() => setOverTooltip(true)}
        onMouseLeave={() => setOverTooltip(false)}
        data-testid={tooltipState?.testId}
        ref={tooltipRef}
        className={clsx(
          'tw:absolute tw:z-9999 tw:max-w-96 tw:rounded-sm tw:bg-black tw:p-2 tw:text-xs tw:text-sq-white',
          {
            'tw:visible tw:animate-fadeIn': show,
            'tw:pointer-events-none tw:invisible': !show,
          },
        )}>
        {tooltipState?.html ? <HTMLTip text={tooltipState.text} /> : tooltipState?.text}
        <div className="tw:absolute tw:h-2.5 tw:w-2.5 tw:rotate-45 tw:bg-black" ref={tooltipArrowRef}></div>
      </div>
    </>
  );
};
